import { createElement, type CSSProperties, type ReactNode } from 'react'
import styles from './Typography.module.scss'

export type TypographyVariant =
  | 'label-sm'
  | 'body-md'
  | 'body-lg'
  | 'title-sm'
  | 'title-md'
  | 'title-lg'
  | 'title-xl'

export type TypographyWeight = 'regular' | 'medium' | 'semibold' | 'bold'

export type TypographyColor =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'placeholder'
  | 'disabled'
  | 'brand-primary'
  | 'error'
  | 'warning'
  | 'success'
  | 'inherit'
  | 'white-inverse'

type IntrinsicElement = keyof React.JSX.IntrinsicElements

export interface TypographyProps {
  variant: TypographyVariant
  /** Defaults follow the design system spec per variant. */
  weight?: TypographyWeight
  /** Defaults to `'primary'`. */
  color?: TypographyColor
  /** Defaults to a sensible HTML element per variant (h2 / p / span). */
  as?: IntrinsicElement
  /** `true` or `1` → ellipsis; `n > 1` → clamp to `n` lines. */
  truncate?: boolean | number
  /**
   * When `true`, swaps the px font-size token for the `clamp()`-bounded rem
   * scale so the user's browser/OS font preference is respected. Use inside
   * scrollable lists and detail bodies; keep `false` (default) for fixed
   * containers like nav bars and badges.
   */
  useRem?: boolean
  /** Optional element id — useful for `aria-labelledby` linkage. */
  id?: string
  children: ReactNode
  className?: string
}

const defaultElementByVariant: Record<TypographyVariant, IntrinsicElement> = {
  'label-sm': 'span',
  'body-md': 'p',
  'body-lg': 'p',
  'title-sm': 'h2',
  'title-md': 'h2',
  'title-lg': 'h2',
  'title-xl': 'h1',
}

const defaultWeightByVariant: Record<TypographyVariant, TypographyWeight> = {
  'label-sm': 'medium',
  'body-md': 'regular',
  'body-lg': 'regular',
  'title-sm': 'semibold',
  'title-md': 'semibold',
  'title-lg': 'semibold',
  'title-xl': 'semibold',
}

export function Typography({
  variant,
  weight,
  color = 'primary',
  as,
  truncate,
  useRem = false,
  id,
  children,
  className,
}: TypographyProps) {
  const Element = as ?? defaultElementByVariant[variant]
  const resolvedWeight = weight ?? defaultWeightByVariant[variant]

  const lineClamp =
    typeof truncate === 'number' && truncate > 1 ? truncate : null

  // `truncate` boolean shorthand and `truncate === 1` both collapse to the
  // single-line ellipsis. Anything > 1 uses the multi-line clamp helper.
  const truncateClass =
    truncate === true || truncate === 1
      ? styles.truncateSingle
      : lineClamp != null
        ? styles.truncateMulti
        : null

  // Pass the clamp count through a CSS variable so the same SCSS rule can
  // serve any line count without generating one class per N.
  const inlineStyle: CSSProperties | undefined =
    lineClamp != null
      ? ({ '--typography-clamp-lines': String(lineClamp) } as CSSProperties)
      : undefined

  const classes = [
    styles.root,
    styles[`variant-${variant}`],
    styles[`weight-${resolvedWeight}`],
    styles[`color-${color}`],
    useRem ? styles.useRem : null,
    truncateClass,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return createElement(
    Element,
    { className: classes, style: inlineStyle, id },
    children,
  )
}
